import { useState, useEffect, useRef, useCallback } from 'react'

import { createVoiceProvider } from '#app/utils/voice-providers/factory.ts'
import {
	type ProviderPhase,
	type IVoiceAgentProvider,
	type TranscriptEvent,
	type ConnectionState,
} from '#app/utils/voice-providers/types.ts'

export interface ICSRData {
	// Administrative Information
	caseId?: string
	reportDate?: string
	reportType?: string

	// Patient Demographics
	patientAge?: string
	patientSex?: string
	patientWeight?: string
	medicalHistory?: string

	// Event Information
	eventDescription?: string
	eventOnsetDate?: string
	severity?: 'mild' | 'moderate' | 'severe'
	seriousness?: boolean
	outcome?: 'recovered' | 'recovering' | 'not_recovered' | 'fatal' | 'unknown'

	// Drug Information
	productName?: string
	dose?: string
	indication?: string
	therapyStartDate?: string
	routeOfAdministration?: string

	// Reporter Information
	reporterName?: string
	reporterType?: 'healthcare_professional' | 'consumer' | 'other'
	reporterContact?: string

	// Assessment
	causalityAssessment?:
		| 'certain'
		| 'probable'
		| 'possible'
		| 'unlikely'
		| 'unrelated'

	// Predicted/Calculated Fields
	predictedMedDRACode?: string
	predictedMedDRATerm?: string
	completeness?: number

	// Timestamps
	lastUpdated?: Date
}

export type ConversationStatus =
	| 'idle'
	| 'connecting'
	| 'greeting'
	| 'collecting_reporter_info'
	| 'collecting_patient_info'
	| 'collecting_event_details'
	| 'collecting_drug_info'
	| 'reviewing_details'
	| 'completing'
	| 'completed'
	| 'error'

export interface VoiceConnectionHook {
	isConnected: boolean
	isConnecting: boolean
	isRecording: boolean
	transcript: string
	icsrData: ICSRData
	conversationStatus: ConversationStatus
	progress: number
	startCall: () => Promise<void>
	endCall: () => void
	toggleMute: () => void
	isMuted: boolean
	error?: string
}

const providerPhaseToConversationStatus: Record<
	ProviderPhase,
	ConversationStatus
> = {
	idle: 'idle',
	connecting: 'connecting',
	greeting: 'greeting',
	collecting_reporter_info: 'collecting_reporter_info',
	collecting_patient_info: 'collecting_patient_info',
	collecting_event_details: 'collecting_event_details',
	collecting_drug_info: 'collecting_drug_info',
	reviewing_details: 'reviewing_details',
	completing: 'completing',
	completed: 'completed',
	error: 'error',
}

function mapPhaseToConversationStatus(
	phase?: string | null,
): ConversationStatus {
	if (!phase) return 'idle'
	if (phase in providerPhaseToConversationStatus) {
		return providerPhaseToConversationStatus[phase as ProviderPhase]
	}
	return 'connecting'
}

export function useVoiceConnection(): VoiceConnectionHook {
	const [isConnected, setIsConnected] = useState(false)
	const [isConnecting, setIsConnecting] = useState(false)
	const [isRecording, setIsRecording] = useState(false)
	const [transcript, setTranscript] = useState('')
	const [icsrData, setIcsrData] = useState<ICSRData>({})
	const [conversationStatus, setConversationStatus] =
		useState<ConversationStatus>('idle')
	const [progress, setProgress] = useState(0)
	const [isMuted, setIsMuted] = useState(false)
	const [error, setError] = useState<string>()
	const [isProviderReady, setIsProviderReady] = useState(false)

	const providerRef = useRef<IVoiceAgentProvider | null>(null)
	const isConnectingRef = useRef(false)

	useEffect(() => {
		let isMounted = true
		const provider = createVoiceProvider('retell')
		providerRef.current = provider

		const handleStateChange = (state: ConnectionState) => {
			if (!isMounted) return

			setIsConnected(state.isConnected)
			setIsRecording(state.isRecording)
			setIsMuted(state.isMuted)
			setIsConnecting(state.isConnecting)
			isConnectingRef.current = state.isConnecting
			setProgress(typeof state.progress === 'number' ? state.progress : 0)
			setConversationStatus(mapPhaseToConversationStatus(state.currentPhase))
		}

		const handleTranscript = (event: TranscriptEvent) => {
			if (!isMounted) return
			setTranscript(event.fullTranscript.trim())
		}

		const handleDataExtracted = (data: Record<string, unknown>) => {
			if (!isMounted) return
			setIcsrData((prev) => ({
				...prev,
				...data,
				lastUpdated: new Date(),
			}))
		}

		const handleError = (err: Error) => {
			if (!isMounted) return

			setError(err.message)
			setIsConnected(false)
			setIsRecording(false)
			setIsConnecting(false)
			isConnectingRef.current = false
			setConversationStatus('error')
		}

		provider.setEventListeners({
			onStateChange: handleStateChange,
			onTranscript: handleTranscript,
			onDataExtracted: handleDataExtracted,
			onError: handleError,
		})

		const initializeProvider = async () => {
			try {
				const supported = await provider.isSupported()
				if (!isMounted) return

				if (!supported) {
					throw new Error(
						'Retell AI is not supported in this browser. Please use a modern browser with WebRTC enabled.',
					)
				}

				const agentId = globalThis.ENV?.RETELL_AGENT_ID
				if (!agentId) {
					throw new Error(
						globalThis.ENV?.MODE === 'development'
							? 'Retell Agent ID not configured. Set RETELL_AGENT_ID in your .env file.'
							: 'Voice agent service is not configured.',
					)
				}

				setError(undefined)
				setIsProviderReady(true)
			} catch (err) {
				if (!isMounted) return

				const message =
					err instanceof Error
						? err.message
						: 'Voice agent service is currently unavailable.'
				setError(message)
				setIsProviderReady(false)
			}
		}

		void initializeProvider()

		return () => {
			isMounted = false
			provider.removeEventListeners()
			provider.destroy()
			providerRef.current = null
		}
	}, [])

	const startCall = useCallback(async () => {
		if (typeof window === 'undefined') {
			return
		}

		if (isConnectingRef.current || isConnected) {
			return
		}

		const provider = providerRef.current
		if (!provider) {
			setError('Voice provider is not available.')
			return
		}

		if (!isProviderReady) {
			setError('Voice agent service is not ready. Please verify configuration.')
			return
		}

		isConnectingRef.current = true
		setIsConnecting(true)
		setError(undefined)
		setConversationStatus('connecting')
		setTranscript('')
		setIcsrData({})
		setProgress(0)

		try {
			await provider.connect({
				debug: globalThis.ENV?.MODE === 'development',
				agentId: globalThis.ENV?.RETELL_AGENT_ID,
				sampleRate: 24000,
			})
		} catch (err) {
			const rawMessage = err instanceof Error ? err.message : String(err)

			let contextualError = 'Failed to initialize voice connection. '
			if (/agent id/i.test(rawMessage)) {
				contextualError +=
					'Retell agent is not configured. Please set RETELL_AGENT_ID and try again.'
			} else if (/token/i.test(rawMessage)) {
				contextualError +=
					'Could not obtain an access token from the server. Please verify API credentials.'
			} else if (/support|browser/i.test(rawMessage)) {
				contextualError +=
					'Your browser does not support the required voice features. Try a modern browser with WebRTC enabled.'
			} else if (/microphone|audio/i.test(rawMessage)) {
				contextualError +=
					'Microphone access is required. Please allow microphone permissions and try again.'
			} else if (/network|connection/i.test(rawMessage)) {
				contextualError +=
					'Network issue detected. Please check your connection and retry.'
			} else {
				contextualError += 'Please try again later.'
			}

			setError(contextualError)
			setIsConnected(false)
			setIsRecording(false)
			setConversationStatus('error')
			setIsConnecting(false)
		} finally {
			isConnectingRef.current = false
		}
	}, [isConnected, isProviderReady])

	const endCall = useCallback(() => {
		setIsConnected(false)
		setIsRecording(false)
		setIsConnecting(false)
		isConnectingRef.current = false
		setConversationStatus('idle')
		setProgress(0)

		const provider = providerRef.current
		if (provider) {
			void provider.disconnect()
		}

		if (Object.keys(icsrData).length > 0) {
			const sessionData = {
				icsrData,
				transcript,
				timestamp: new Date().toISOString(),
				conversationId: `session_${Date.now()}`,
			}
			try {
				localStorage.setItem('icsr_session_data', JSON.stringify(sessionData))
			} catch {
				// Swallow storage errors (e.g. quota exceeded, private mode)
			}
		}
	}, [icsrData, transcript])

	const toggleMute = () => {
		const provider = providerRef.current
		if (!provider) return
		provider.toggleMute()
	}

	useEffect(() => {
		const savedSessionData = localStorage.getItem('icsr_session_data')
		if (!savedSessionData) return

		try {
			// Future enhancement: offer to restore saved data
			// const session = JSON.parse(savedSessionData)
		} catch {
			// Ignore corrupted session payloads
		}
	}, [])

	useEffect(() => {
		return () => {
			endCall()
		}
	}, [endCall])

	return {
		isConnected,
		isConnecting,
		isRecording,
		transcript,
		icsrData,
		conversationStatus,
		progress,
		startCall,
		endCall,
		toggleMute,
		isMuted,
		error,
	}
}
